import React, { useState, useMemo } from 'react';
import { ArrowRight, Plus, ShieldCheck, Boxes, Puzzle, Users, MessageSquare, Lock, BadgeCheck } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import SEO from '../components/SEO';
import PluginSecurityPosture from '../components/PluginSecurityPosture';
import PluginCard from '../components/PluginCard';
import SubmitPluginModal from '../components/SubmitPluginModal';
import BuildWithUs from '../components/BuildWithUs';
import { Avatar } from '../components/community/MemberCard';
import InboxFab from '../components/community/InboxFab';
import { COMMUNITY_MEMBERS, COMMUNITY_STATS } from '../data/communityMembers';
import { PLUGINS, DEVELOPERS_BUILDING } from '../data/plugins';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';

const PluginsPage = () => {
  const [submitOpen, setSubmitOpen] = useState(false);
  const [filter, setFilter] = useState('All');
  const navigate = useNavigate();
  const { isMember, loading: authLoading } = useAuth();

  const categories = useMemo(() => {
    const groups = new Set(PLUGINS.map((p) => p.category.split(' · ')[0]));
    return ['All', ...Array.from(groups)];
  }, []);

  const visible = filter === 'All' ? PLUGINS : PLUGINS.filter((p) => p.category.startsWith(filter));
  const categoryCount = categories.length - 1;

  return (
    <div className="min-h-screen bg-white">
      <SEO
        title="Forsyt Micro SaaS Store"
        description="Start building your own Micro SaaS with Forsyt. Describe your idea, answer a few questions, and our team helps you scope, fund and launch it — plus a marketplace of focused tools for revenue teams."
        path="/plugins"
      />
      <Header />

      <main>
        {/* Hero */}
        <section className="pt-24 pb-14 border-b border-slate-100 bg-gradient-to-b from-slate-50/60 to-white">
          <div className="max-w-4xl mx-auto px-6 lg:px-8">
            <div className="text-center max-w-2xl mx-auto">
              <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-500 mb-4">
                <Boxes className="w-4 h-4" /> Micro SaaS Store
              </div>
              <h1 className="text-4xl lg:text-5xl font-extrabold tracking-tight text-slate-900 leading-[1.05]">
                Have an idea? Build a Micro SaaS with us.
              </h1>
              <p className="mt-4 text-lg text-slate-600 leading-relaxed">
                You bring the idea. We turn it into a PRD in minutes, schedule a call, and put our
                marketing money behind it to help you launch on Forsyt.
              </p>
            </div>

            <div className="mt-10">
              <BuildWithUs />
            </div>

            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <button
                onClick={() => setSubmitOpen(true)}
                data-testid="plugins-submit-cta"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full border border-slate-200 text-slate-900 text-sm font-semibold hover:bg-slate-50 transition-colors"
              >
                <Plus className="w-4 h-4" /> Submit a finished tool
              </button>
              {!authLoading && !isMember && (
                <button
                  onClick={() => navigate('/community')}
                  data-testid="plugins-hero-apply"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-emerald-600 text-white text-sm font-semibold hover:bg-emerald-500 transition-colors"
                >
                  <Users className="w-4 h-4" /> Join the community
                </button>
              )}
              <a
                href="#catalogue"
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full text-slate-600 text-sm font-semibold hover:text-slate-900 transition-colors"
              >
                Browse the store <ArrowRight className="w-4 h-4" />
              </a>
            </div>
          </div>
        </section>

        {/* Catalogue + persistent community rail */}
        <section id="catalogue" className="py-16">
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
              {/* Community left-rail */}
              <aside className="lg:col-span-3">
                <CommunityRail isMember={isMember} navigate={navigate} />
              </aside>

              {/* Plugins column */}
              <div className="lg:col-span-9">
                <div className="flex flex-wrap items-center gap-2 mb-8" data-testid="plugin-filters">
                  {categories.map((c) => (
                    <button
                      key={c}
                      onClick={() => setFilter(c)}
                      data-testid={`plugin-filter-${c.toLowerCase().replace(/\s+/g, '-')}`}
                      className={`px-4 py-2 rounded-full text-sm font-medium border transition-colors ${
                        filter === c
                          ? 'bg-slate-900 text-white border-slate-900'
                          : 'bg-white text-slate-600 border-slate-200 hover:border-slate-300'
                      }`}
                    >
                      {c}
                    </button>
                  ))}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6" data-testid="plugin-grid">
                  {visible.map((p) => (
                    <PluginCard key={p.slug} plugin={p} />
                  ))}

                  {/* Submit tile */}
                  <button
                    onClick={() => setSubmitOpen(true)}
                    data-testid="plugin-submit-tile"
                    className="group flex flex-col items-center justify-center text-center gap-3 min-h-[360px] bg-slate-50/60 border border-dashed border-slate-300 rounded-2xl p-8 hover:border-slate-400 hover:bg-slate-50 transition-colors"
                  >
                    <span className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-white border border-slate-200 text-slate-500 group-hover:text-slate-900 transition-colors">
                      <Puzzle className="w-7 h-7" strokeWidth={1.6} />
                    </span>
                    <span className="text-lg font-bold text-slate-900">Build the next one</span>
                    <span className="text-sm text-slate-500 leading-relaxed max-w-xs">
                      Have a capability your team keeps rebuilding by hand? Submit it and we will help you ship it as a plugin.
                    </span>
                    <span className="mt-1 inline-flex items-center gap-1.5 text-sm font-semibold text-slate-900">
                      Submit your plugin <ArrowRight className="w-4 h-4 group-hover:translate-x-0.5 transition-transform" />
                    </span>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Revenue Community cross-promo */}
        <section className="py-16 border-t border-slate-100 bg-white">
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="rounded-3xl bg-slate-900 text-white p-8 lg:p-12 relative overflow-hidden">
              <div className="absolute inset-0 opacity-[0.06]" style={{ backgroundImage: 'radial-gradient(circle at 1px 1px, #fff 1px, transparent 0)', backgroundSize: '26px 26px' }} />
              <div className="relative grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                <div className="lg:col-span-7">
                  <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-400 mb-4">
                    <Users className="w-4 h-4" /> Built with the Revenue Community
                  </div>
                  <h2 className="text-3xl lg:text-4xl font-extrabold tracking-tight leading-tight">
                    Every plugin was requested by a real revenue leader.
                  </h2>
                  <p className="mt-4 text-slate-300 leading-relaxed max-w-xl">
                    The people behind these plugins are in the room. Join the vetted community of {COMMUNITY_STATS.members} Chief Revenue Officers and revenue leaders — apply to join, then message plugin creators and peers directly.
                  </p>
                  <div className="mt-6 flex flex-wrap items-center gap-3">
                    <button onClick={() => navigate('/community')} data-testid="plugins-community-apply" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-slate-900 text-sm font-semibold hover:bg-slate-100 transition-colors">
                      Apply to join <ArrowRight className="w-4 h-4" />
                    </button>
                    <button onClick={() => navigate('/community')} className="inline-flex items-center gap-2 px-5 py-3 rounded-full border border-white/20 text-white text-sm font-semibold hover:bg-white/10 transition-colors">
                      See who's inside
                    </button>
                  </div>
                </div>
                <div className="lg:col-span-5">
                  <div className="flex flex-wrap gap-3">
                    {COMMUNITY_MEMBERS.slice(0, 6).map((m) => (
                      <div key={m.id} className="flex items-center gap-2 bg-white/10 border border-white/10 rounded-full pl-1 pr-3 py-1">
                        <Avatar member={m} size={30} />
                        <span className="text-xs font-medium text-white truncate max-w-[120px]">{m.name}</span>
                      </div>
                    ))}
                    <div className="flex items-center gap-2 bg-white/5 border border-white/10 rounded-full px-4 py-1 text-xs text-slate-300">
                      +{COMMUNITY_STATS.members - 6} more inside
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Submit strip */}
        <section className="py-16 border-t border-slate-100 bg-slate-900">
          <div className="max-w-6xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              <div className="lg:col-span-8">
                <div className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.18em] text-slate-400 mb-4">
                  <ShieldCheck className="w-4 h-4" /> For builders
                </div>
                <h2 className="text-4xl font-extrabold text-white tracking-tight leading-tight">
                  Your plugin runs sandboxed, scoped, and reviewed.
                </h2>
                <p className="mt-4 text-slate-300 leading-relaxed max-w-2xl">
                  Plugins receive typed bands and hashed IDs, never raw customer records. Every plugin declares its
                  scopes up front and is reviewed before it reaches the store. You build the capability, Forsyt handles
                  the runtime, isolation, and distribution.
                </p>
              </div>
              <div className="lg:col-span-4 lg:text-right">
                <button
                  onClick={() => setSubmitOpen(true)}
                  data-testid="plugins-submit-strip-cta"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-white text-slate-900 text-sm font-semibold hover:bg-slate-100 transition-colors"
                >
                  <Plus className="w-4 h-4" /> Submit your plugin
                </button>
              </div>
            </div>
          </div>
        </section>

        <PluginSecurityPosture />
      </main>

      <Footer />
      <SubmitPluginModal open={submitOpen} onClose={() => setSubmitOpen(false)} />
      <InboxFab />
    </div>
  );
};

const Stat = ({ value, label }) => (
  <div>
    <div className="text-3xl font-extrabold text-slate-900 tracking-tight">{value}</div>
    <div className="text-xs font-medium uppercase tracking-wider text-slate-500 mt-1">{label}</div>
  </div>
);

const CommunityRail = ({ isMember, navigate }) => (
  <div className="lg:sticky lg:top-6" data-testid="plugins-community-rail">
    <div className="rounded-2xl border border-slate-200 bg-white overflow-hidden">
      <div className="px-4 py-4 bg-slate-900 text-white">
        <div className="inline-flex items-center gap-2 text-[10px] font-bold uppercase tracking-[0.16em] text-slate-300 mb-1">
          <Users className="w-3.5 h-3.5" /> Revenue Community
        </div>
        <div className="text-lg font-extrabold leading-tight">{COMMUNITY_STATS.members} vetted leaders</div>
        <p className="text-xs text-slate-300 mt-1 leading-relaxed">The people who requested these plugins are in the room.</p>
        {isMember ? (
          <button
            onClick={() => navigate('/community/inbox')}
            data-testid="rail-open-inbox"
            className="mt-3 w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-white text-slate-900 text-xs font-bold hover:bg-slate-100 transition-colors"
          >
            <MessageSquare className="w-3.5 h-3.5" /> Open inbox
          </button>
        ) : (
          <button
            onClick={() => navigate('/community')}
            data-testid="rail-apply-cta"
            className="mt-3 w-full inline-flex items-center justify-center gap-2 px-3 py-2.5 rounded-lg bg-white text-slate-900 text-xs font-bold hover:bg-slate-100 transition-colors"
          >
            Apply to join <ArrowRight className="w-3.5 h-3.5" />
          </button>
        )}
      </div>
      <div className="p-2 max-h-[520px] overflow-y-auto">
        {COMMUNITY_MEMBERS.slice(0, 10).map((m) => (
          <button
            key={m.id}
            onClick={() => navigate(isMember ? '/community/inbox' : '/community')}
            data-testid={`rail-member-${m.id}`}
            className="w-full flex items-center gap-3 p-2 rounded-xl hover:bg-slate-50 transition-colors text-left"
          >
            <Avatar member={m} size={38} />
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-1">
                <span className="text-sm font-semibold text-slate-900 truncate">{m.name}</span>
                <BadgeCheck className="w-3.5 h-3.5 text-emerald-500 shrink-0" />
              </div>
              <div className="text-[11px] text-slate-500 truncate">{m.company}</div>
            </div>
            {isMember ? (
              <MessageSquare className="w-3.5 h-3.5 text-slate-400 shrink-0" />
            ) : (
              <Lock className="w-3.5 h-3.5 text-slate-300 shrink-0" />
            )}
          </button>
        ))}
      </div>
      <div className="px-4 py-3 border-t border-slate-100 bg-slate-50 text-center">
        <button
          onClick={() => navigate('/community')}
          data-testid="rail-see-all"
          className="text-xs font-semibold text-slate-700 hover:text-slate-900 inline-flex items-center gap-1"
        >
          {isMember ? 'See all members' : `+${COMMUNITY_STATS.members - 10} more inside`} <ArrowRight className="w-3 h-3" />
        </button>
      </div>
    </div>
  </div>
);

export default PluginsPage;
